import axios from 'axios';
// config
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject((error.response && error.response.data) || 'Something went wrong')
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const API_ENDPOINTS = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/api/auth/me',
    login: '/api/auth/login',
    pair: '/api/auth/pair',
    register: '/api/auth/register',
  },
  // Dev-only "view as student" for super admins (#605); 404 unless enabled
  studentImpersonation: {
    config: '/api/dev/student-impersonation/config',
    start: '/api/dev/student-impersonation/start',
    stop: '/api/dev/student-impersonation/stop',
  },
  student: {
    list: '/api/student/list',
    details: (asn) => `/api/students/${asn}`,
    profileReview: (id) => `/api/studentprofilereview/${id}`,
    profileReviewParent: (id) => `/api/studentprofilereview/parent/${id}`,
    profileReviewParentRoutingPreview: (id) =>
      `/api/studentprofilereview/parent/${id}/routing-preview`,
    addressRoutingPreview: (asn) => `/api/students/${asn}/address/routing-preview`,
    profileReviewAdmin: (id) => `/api/studentprofilereview/admin/${id}`,
    interestsList: '/api/students/interests-list',
    contacts: (asn) => `/api/students/${asn}/contacts`,
    careerSummary: (asn) => `/api/students/${asn}/career-summary`,
    industrySuggestion: (asn) => `/api/students/${asn}/industry-suggestion`,
    comment: (asn) => `/api/students/${asn}/comment`,
    editComment: (asn, id) => `/api/students/${asn}/comment/${id}`,
    visitLog: (asn) => `/api/students/${asn}/visit-log`,
    editVisitLog: (asn, id) => `/api/students/${asn}/visit-log/${id}`,
    nextVisit: (asn) => `/api/students/${asn}/next-visit`,
    documents: (asn) => `/api/students/${asn}/documents`,
    prerequisite: (asn, code) => `/api/students/${asn}/prerequisites/${code}`,
    downloadCareerProfile: (asn) => `/api/students/${asn}/career-profile/download`,
    todos: (asn) => `/api/students/${asn}/todos`,
    todo: (asn, id) => `/api/students/${asn}/todos/${id}`,
    homeEdComments: (asn) => `/api/students/${asn}/home-ed-comments`,
    homeEdComment: (asn) => `/api/students/${asn}/home-ed-comment`,
    editHomeEdComment: (asn, id) => `/api/students/${asn}/home-ed-comment/${id}`,
    deleteHomeEdComment: (asn, id) => `/api/students/${asn}/home-ed-comment/${id}`,
  },
  career: {
    myTodos: '/api/career/todos/mine',
    todoAssignees: '/api/career/todos/assignees',
    caseload: '/api/career/caseload',
    nextVisits: '/api/career/next-visits',
  },
  admin: {
    facilitators: '/api/admin/facilitators',
    facilitatorStudents: (externalId) => `/api/admin/facilitators/${externalId}/students`,
    homeEdStats: '/api/admin/home-ed-stats',
    homeEdUnassigned: (page, limit) => `/api/admin/home-ed-unassigned?page=${page}&limit=${limit}`,
    homeEdAllStudents: '/api/admin/home-ed-all-students',
    facilitatorSyncPreview: '/api/admin/facilitator-sync-preview',
    facilitatorSyncApply: '/api/admin/facilitator-sync-apply',
    processEmployees: '/api/admin/process-employees',
    processEmployeesRuns: '/api/admin/process-employees/runs',
    processEmployeesRun: (runId) => `/api/admin/process-employees/runs/${runId}`,
    processEmployeesDryRun: (query) => `/api/admin/process-employees/dry-run?query=${encodeURIComponent(query)}`,
  },
  homeEdFamilies: {
    list: '/api/home-ed-families',
    create: '/api/home-ed-families',
    get: (id) => `/api/home-ed-families/${id}`,
    update: (id) => `/api/home-ed-families/${id}`,
    delete: (id) => `/api/home-ed-families/${id}`,
    addStudent: (id) => `/api/home-ed-families/${id}/students`,
    removeStudent: (id, asn) => `/api/home-ed-families/${id}/students/${asn}`,
    assignFacilitator: (id) => `/api/home-ed-families/${id}/facilitator`,
    addContact: (id) => `/api/home-ed-families/${id}/contacts`,
    removeContact: (id, contactId) => `/api/home-ed-families/${id}/contacts/${contactId}`,
    byStudent: (asn) => `/api/home-ed-families/by-student/${asn}`,
    byFacilitator: (externalId) => `/api/home-ed-families/by-facilitator/${externalId}`,
    homeEdStudents: '/api/home-ed-families/home-ed-students',
    searchContacts: '/api/home-ed-families/search-contacts',
  },
  profileReview: {
    init: '/api/studentprofilereview',
    export: '/api/studentprofilereview/export',
  },
  homeEdRenewal: {
    get: (id) => `/api/homeedrenewal/${id}`,
    parent: (id) => `/api/homeedrenewal/parent/${id}`,
    admin: (id) => `/api/homeedrenewal/admin/${id}`,
    delete: (id) => `/api/homeedrenewal/${id}`,
    download: (id) => `/api/homeedrenewal/${id}/download`,
    years: '/api/homeedrenewal/years',
    newYear: '/api/homeedrenewal/year',
    byStudent: (asn) => `/api/homeedrenewal/student/${asn}`,
  },
  homeEdProgramPlan: {
    create: '/api/homeedprogramplan',
    get: (id) => `/api/homeedprogramplan/${id}`,
    parent: (id) => `/api/homeedprogramplan/parent/${id}`,
    admin: (id) => `/api/homeedprogramplan/admin/${id}`,
    download: (id) => `/api/homeedprogramplan/${id}/download`,
    byStudent: (asn) => `/api/homeedprogramplan/student/${asn}`,
  },
  acceptanceLetter: {
    create: '/api/acceptance-letter',
    byStudent: (asn, schoolYear) => `/api/acceptance-letter/by-student/${asn}/${schoolYear}`,
    download: (id) => `/api/acceptance-letter/${id}/download`,
  },
  enroll: '/api/enroll',
  homeEdRegistration: {
    create: '/api/homeedregistration',
    list: '/api/homeedregistration/list',
    details: (id) => `/api/homeedregistration/${id}`,
    download: (id) => `/api/homeedregistration/${id}/download`,
    identityDocumentDownload: (id) =>
      `/api/homeedregistration/${id}/identity-document/download`,
  },
  enrollment: {
    list: '/api/enrollment/list',
    details: (id) => `/api/enrollment/${id}`,
    report: '/api/enrollment/report',
  },
  assessments: {
    list: (type) => `/api/assessments/list/${type}`,
    details: (id) => `/api/assessments/${id}`,
    type: (type, version) => `/api/assessments/type/${type}/${version}`,
    update: (id) => `/api/assessments/${id}`,
    delete: () => `/api/assessments`,
    schoolYears: (type) => `/api/assessments/schoolYears/${type}`,
    wratReference: () => `/api/assessments/wratReference`,
  },
  busRoutes: {
    list: '/api/bus-routing/bus-routes',
    details: (routeId) => `/api/bus-routing/bus-routes/${routeId}`,
    rebuild: (routeId) => `/api/bus-routing/bus-routes/${routeId}/rebuild`,
    update: (routeId) => `/api/bus-routing/bus-routes/${routeId}`,
    runs: (busNumber) => `/api/bus-routing/runs/${busNumber}`,
    assignDevice: (routeId) => `/api/bus-routing/bus-routes/${routeId}/assign-device`,
    removeDevice: (routeId) => `/api/bus-routing/bus-routes/${routeId}/remove-device`,
    rejectRebuild: (routeId) => `/api/bus-routing/bus-routes/${routeId}/reject-rebuild`,
  },
  runs: {
    assignedRoutes: '/api/busruns/assigned-routes',
    active: (routeId) => `/api/bus-routing/runs/active/${routeId}`,
    initialize: `/api/bus-routing/runs/initialize`,
    pickup: (runId) => `/api/bus-routing/runs/${runId}/pickup`,
    dropoff: (runId) => `/api/bus-routing/runs/${runId}/dropoff`,
    clearStatus: (runId) => `/api/bus-routing/runs/${runId}/clear-status`,
    completeStop: (runId) => `/api/bus-routing/runs/${runId}/complete-stop`,
    finalize: (runId) => `/api/bus-routing/runs/${runId}/finalize`,
    abort: (runId) => `/api/bus-routing/runs/${runId}/abort`,
  },
  address: {
    query: '/api/addresslookup',
  },
  vehicle: '/api/vehicle',
  vehicleType: '/api/vehicle/type',
  component: '/api/vehicle/component',
  componentType: '/api/vehicle/component/type',
  part: '/api/part',
  workOrder: '/api/workorder',
  purchaseOrder: '/api/purchaseorder',
  routingRequest: '/api/bus-routing/routingrequests',
  crossboundaryRequest: '/api/bus-routing/crossboundary',
  devices: {
    list: '/api/devices',
    details: (deviceId) => `/api/devices/${deviceId}`,
    pair: '/api/devices/pair',
  },
  // Student-facing off-campus API (#603): answers for the signed-in student only
  offCampusMe: {
    placement: '/api/off-campus/me/placement',
    placements: '/api/off-campus/me/placements',
    placementById: (id) => `/api/off-campus/me/placements/${id}`,
    survey: '/api/off-campus/me/survey',
    selfEvaluation: (learningPlanId) => `/api/off-campus/me/self-evaluations/${learningPlanId}`,
  },
  // The rest of the student's dashboard (#633): answers for the signed-in student only
  me: {
    career: '/api/me/career',
    todos: '/api/me/todos',
    todo: (id) => `/api/me/todos/${id}`,
  },
  offCampus: {
    worksites: '/api/off-campus/worksites',
    worksite: (id) => `/api/off-campus/worksites/${id}`,
    worksiteArchive: (id) => `/api/off-campus/worksites/${id}/archive`,
    worksiteUnarchive: (id) => `/api/off-campus/worksites/${id}/unarchive`,
    worksiteInspections: (id) => `/api/off-campus/worksites/${id}/inspections`,
    worksiteDocuments: (id) => `/api/off-campus/worksites/${id}/documents`,
    worksiteDocument: (id, docId) => `/api/off-campus/worksites/${id}/documents/${docId}`,
    worksiteDocumentUrl: (id, docId) => `/api/off-campus/worksites/${id}/documents/${docId}/url`,
    studentPlacement: (asn) => `/api/off-campus/students/${asn}/placement`,
    studentPlacements: (asn) => `/api/off-campus/students/${asn}/placements`,
    referrals: '/api/off-campus/referrals',
    referral: (id) => `/api/off-campus/referrals/${id}`,
    referralStatus: (id) => `/api/off-campus/referrals/${id}/status`,
    coordinators: '/api/off-campus/coordinators',
    placements: '/api/off-campus/placements',
    industries: '/api/off-campus/industries',
    industry: (id) => `/api/off-campus/industries/${id}`,
    placement: (id) => `/api/off-campus/placements/${id}`,
    placementStatus: (id) => `/api/off-campus/placements/${id}/status`,
    placementInternalComment: (id) => `/api/off-campus/placements/${id}/internal-comment`,
    // A Green Certificate placement's completed form and GINA date
    placementGcRegistrationSignedDownload: (id) => `/api/off-campus/placements/${id}/gc-registration/signed-download`,
    placementUploadSignedGcRegistration: (id) => `/api/off-campus/placements/${id}/gc-registration/upload-signed`,
    placementGcRegistrationGina: (id) => `/api/off-campus/placements/${id}/gc-registration/gina`,
    placementTesterMarks: (id) => `/api/off-campus/placements/${id}/tester-marks`,
    placementTesterMark: (id, markId) => `/api/off-campus/placements/${id}/tester-marks/${markId}`,
    placementGenerateWpa: (id) => `/api/off-campus/placements/${id}/wpa/generate`,
    placementWpaDownload: (id) => `/api/off-campus/placements/${id}/wpa/download`,
    placementWpaSignedDownload: (id) => `/api/off-campus/placements/${id}/wpa/signed-download`,
    placementWpaRecipients: (id) => `/api/off-campus/placements/${id}/wpa/recipients`,
    placementStudentContacts: (id) => `/api/off-campus/placements/${id}/student-contacts`,
    placementSendWpa: (id) => `/api/off-campus/placements/${id}/wpa/send`,
    placementUploadSignedWpa: (id) => `/api/off-campus/placements/${id}/wpa/upload-signed`,
    placementWpaCancelDocusign: (id) => `/api/off-campus/placements/${id}/wpa/cancel-docusign`,
    placementWpaRefreshStatus: (id) => `/api/off-campus/placements/${id}/wpa/refresh-status`,
    placementOnboardingEmail: (id) => `/api/off-campus/placements/${id}/onboarding-email`,
    placementOnboardingEmailPreview: (id) => `/api/off-campus/placements/${id}/onboarding-email/preview`,
    emailTemplates: '/api/off-campus/email-templates',
    settings: '/api/off-campus/settings',
    // What the signed-in user can do in Off Campus (coordinator, secretary, counsellor)
    access: '/api/off-campus/access',
    emailTemplate: (key) => `/api/off-campus/email-templates/${encodeURIComponent(key)}`,
    emailTemplatePreview: (key) => `/api/off-campus/email-templates/${encodeURIComponent(key)}/preview`,
    emailTemplateAttachments: (key) => `/api/off-campus/email-templates/${encodeURIComponent(key)}/attachments`,
    docusignConsentUrl: '/api/off-campus/docusign/consent-url',
    studentSearch: (q) => `/api/off-campus/students/search?q=${encodeURIComponent(q)}`,
    placementSurvey: (id) => `/api/off-campus/placements/${id}/survey`,
    placementSurveyApprove: (id) => `/api/off-campus/placements/${id}/survey/approve`,
    placementSurveyReturn: (id) => `/api/off-campus/placements/${id}/survey/return`,
    placementArchive: (id) => `/api/off-campus/placements/${id}/archive`,
    placementUnarchive: (id) => `/api/off-campus/placements/${id}/unarchive`,
    placementLearningPlans: (id) => `/api/off-campus/placements/${id}/learning-plans`,
    placementNextSegment: (id) => `/api/off-campus/placements/${id}/next-segment`,
    learningPlan: (id) => `/api/off-campus/learning-plans/${id}`,
    learningPlans: '/api/off-campus/learning-plans',
    learningPlanHours: (id) => `/api/off-campus/learning-plans/${id}/hours`,
    learningPlanHoursEntry: (planId, entryId) => `/api/off-campus/learning-plans/${planId}/hours/${entryId}`,
    learningPlanClose: (id) => `/api/off-campus/learning-plans/${id}/close`,
    learningPlanReopen: (id) => `/api/off-campus/learning-plans/${id}/reopen`,
    // A segment's close-out, once its evaluations are in (#624)
    learningPlanCloseOut: (id) => `/api/off-campus/learning-plans/${id}/close-out`,
    segmentsAwaitingClose: '/api/off-campus/segments/awaiting-close',
    // Segments whose employer and self evaluations are in, waiting on the coordinator's
    segmentsAwaitingCoordinatorEval: '/api/off-campus/segments/awaiting-coordinator-evaluation',
    learningPlanEmployerEval: (id) => `/api/off-campus/learning-plans/${id}/employer-evaluation`,
    learningPlanCoordinatorEval: (id) => `/api/off-campus/learning-plans/${id}/coordinator-evaluation`,
    learningPlanSelfEval: (id) => `/api/off-campus/learning-plans/${id}/self-evaluation`,
    learningPlanSelfEvalReturn: (id) => `/api/off-campus/learning-plans/${id}/self-evaluation/return`,
    learningPlanSelfEvalResend: (id) => `/api/off-campus/learning-plans/${id}/self-evaluation/resend`,
    employerEvaluation: (id) => `/api/off-campus/employer-evaluations/${id}`,
    employerEvaluationComplete: (id) => `/api/off-campus/employer-evaluations/${id}/complete`,
    employerEvaluationReopen: (id) => `/api/off-campus/employer-evaluations/${id}/reopen`,
    employerEvaluationRegenerateToken: (id) =>
      `/api/off-campus/employer-evaluations/${id}/regenerate-token`,
    coordinatorEvaluations: '/api/off-campus/coordinator-evaluations',
    coordinatorEvaluation: (id) => `/api/off-campus/coordinator-evaluations/${id}`,
    publicEmployerEval: (token) => `/api/public/employer-evaluation/${token}`,
  },
};
